import { useEffect, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
  ArcElement,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
  ArcElement
);

const PriceChart = ({
  labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  data = [420, 340, 460, 230, 470, 300, 360],
  lineColor = "#2563eb", // blue
  gradientStart = "rgba(37, 99, 235, 0.15)",
  gradientEnd = "rgba(37, 99, 235, 0)",
  pointerIndex = -1,
  pointerColor = "#2563eb", // circle color
  pointerLabelBg = "#000", // tooltip background
  pointerLabelTextColor = "#fff", // tooltip text color
}) => {
  const chartRef = useRef(null);

  const [chartData, setChartData] = useState({
    labels,
    datasets: [
      {
        label: "",
        data,
        borderColor: lineColor,
        borderWidth: 2,
        pointRadius: 0,
        fill: true,
        tension: 0.45,
        backgroundColor: gradientStart,
      },
    ],
  });

  useEffect(() => {
    if (!chartRef.current) return;
    const chart = chartRef.current;
    const ctx = chart.ctx;
    const gradient = ctx.createLinearGradient(0, 0, 0, chart.chartArea.bottom);
    gradient.addColorStop(0, gradientStart);
    gradient.addColorStop(1, gradientEnd);

    setChartData((prev) => ({
      ...prev,
      datasets: prev.datasets.map((ds) => ({
        ...ds,
        backgroundColor: gradient,
      })),
    }));
  }, [gradientEnd, gradientStart]);

  // Custom plugin for pointer & tooltip
  const pointerPlugin = {
    id: "pointerPlugin",
    afterDatasetsDraw(chart) {
      const {
        ctx,
        chartArea: { bottom },
        scales: { x, y },
      } = chart;

      if (pointerIndex < 0 || pointerIndex >= data.length) return;

      const value = data[pointerIndex];
      const xPos = x.getPixelForValue(pointerIndex);
      const yPos = y.getPixelForValue(value);

      ctx.save();

      // Vertical line
      ctx.beginPath();
      ctx.moveTo(xPos, yPos);
      ctx.lineTo(xPos, bottom);
      ctx.lineWidth = 1;
      ctx.strokeStyle = `${pointerColor}50`;
      ctx.stroke();

      // Circle with white border
      ctx.beginPath();
      ctx.arc(xPos, yPos - 6, 6, 0, 2 * Math.PI);
      ctx.fillStyle = pointerColor;
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#fff";
      ctx.stroke();

      // Tooltip
      const label = `$${value}`;
      ctx.font = "12px Arial";
      const textWidth = ctx.measureText(label).width;
      const paddingX = 8;
      const paddingY = 2;
      const tooltipWidth = textWidth + paddingX * 2;
      const tooltipHeight = 24 + paddingY * 2;
      const tooltipX = xPos - tooltipWidth / 2;
      const tooltipY = yPos - tooltipHeight - 22;

      // Rounded tooltip background
      ctx.fillStyle = pointerLabelBg;
      ctx.beginPath();
      ctx.moveTo(tooltipX + 4, tooltipY);
      ctx.lineTo(tooltipX + tooltipWidth - 4, tooltipY);
      ctx.quadraticCurveTo(
        tooltipX + tooltipWidth,
        tooltipY,
        tooltipX + tooltipWidth,
        tooltipY + 4
      );
      ctx.lineTo(tooltipX + tooltipWidth, tooltipY + tooltipHeight - 4);
      ctx.quadraticCurveTo(
        tooltipX + tooltipWidth,
        tooltipY + tooltipHeight,
        tooltipX + tooltipWidth - 4,
        tooltipY + tooltipHeight
      );
      ctx.lineTo(tooltipX + 4, tooltipY + tooltipHeight);
      ctx.quadraticCurveTo(
        tooltipX,
        tooltipY + tooltipHeight,
        tooltipX,
        tooltipY + tooltipHeight - 4
      );
      ctx.lineTo(tooltipX, tooltipY + 4);
      ctx.quadraticCurveTo(tooltipX, tooltipY, tooltipX + 4, tooltipY);
      ctx.closePath();
      ctx.fill();

      // Pointer triangle beneath tooltip
      const pointerSize = 6;
      const triangleTop = tooltipY + tooltipHeight; // place directly under tooltip
      ctx.beginPath();
      ctx.moveTo(xPos - pointerSize, triangleTop);
      ctx.lineTo(xPos + pointerSize, triangleTop);
      ctx.lineTo(xPos, triangleTop + pointerSize);
      ctx.closePath();
      ctx.fill();

      // Tooltip text
      ctx.fillStyle = pointerLabelTextColor;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(label, xPos, tooltipY + tooltipHeight / 2);

      ctx.restore();
    },
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    scales: {
      x: {
        grid: { display: false }, // REMOVE X-AXIS LINES
        ticks: { color: "#6b7280", font: { size: 12 } },
      },
      y: {
        min: 0,
        max: 600, // INCREASE TO ADD MORE SPACING
        offset: false, // ADDS SPACING BETWEEN LABELS AND AXIS
        ticks: {
          stepSize: 100,
          callback: (value) =>
            [0, 100, 200, 300, 400].includes(value) ? value : "",
          color: "#6b7280",
          font: { size: 12 },
        },
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <div className="h-[calc(100%-50px)]">
      <Line
        ref={chartRef}
        data={chartData}
        options={lineOptions}
        plugins={[pointerPlugin]}
      />
    </div>
  );
};

export default PriceChart;
